import React, { useRef, createContext, useContext, useMemo } from 'react';
import { debounce } from 'lodash-es';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

// Split context: data changes on every hover, API is stable.
// Consumers that only trigger hovers (ListingEntry) use the API context
// and never re-render when hoveredHotelId changes.
type HoverData = Readonly<{ hoveredHotelId: string | null }>;
type HoverApi = Readonly<{ onHoverStart: (hotelId: string) => void; onHoverEnd: () => void }>;

const HoverDataContext = createContext<HoverData | null>(null);
const HoverApiContext = createContext<HoverApi | null>(null);

export const useAccommodationMapListSyncData = () => {
  const context = useContext(HoverDataContext);
  if (!context) {
    throw new Error('useAccommodationMapListSyncData must be used within AccommodationMapListSync');
  }
  return context;
};

export const useAccommodationMapListSyncApi = () => {
  const context = useContext(HoverApiContext);
  if (!context) {
    throw new Error('useAccommodationMapListSyncApi must be used within AccommodationMapListSync');
  }
  return context;
};

// Convenience hook that returns both (backwards-compatible)
export const useAccommodationMapListSync = () => {
  const data = useAccommodationMapListSyncData();
  const api = useAccommodationMapListSyncApi();
  return { ...data, ...api };
};

interface AccommodationMapListSyncProps {
  children: React.ReactNode;
}

/**
 * This component is used to sync hotel id/events/actions between the map and the list.
 * To sync actionable events between the map and the list.
 *
 * Example:
 * - When the user hovers over a hotel on the map, the hotel id is synced to the list.
 * - When the user clicks on a hotel on the map, the hotel id is synced to the list.
 * - When the user hovers over a hotel in the list, the hotel id is synced to the map.
 * - When the user clicks on a hotel in the list, the hotel id is synced to the map.
 *
 * This is used to ensure that the user sees the same hotel in both the map and the list.
 */
export const AccommodationMapListSync = React.memo<AccommodationMapListSyncProps>(({ children }) => {
  const hoveredHotelIdRef = useRef<string | null>(null);
  const [hoveredHotelId, setHoveredHotelId] = React.useState<string | null>(null);

  // Debounce hover start to reduce rapid state updates when moving mouse quickly across listings
  const debouncedSetHoveredHotelId = useMemo(
    () =>
      debounce((hotelId: string) => {
        setHoveredHotelId(hotelId);
      }, 50),
    [],
  );

  const onHoverStart = useEventCallback((hotelId: string) => {
    if (hoveredHotelIdRef.current === hotelId) {
      return;
    }
    // Eagerly update ref so the guard above deduplicates during rapid hover sequences
    hoveredHotelIdRef.current = hotelId;
    debouncedSetHoveredHotelId(hotelId);
  });

  const onHoverEnd = useEventCallback(() => {
    debouncedSetHoveredHotelId.cancel();
    if (hoveredHotelIdRef.current !== null) {
      hoveredHotelIdRef.current = null;
      setHoveredHotelId(null);
    }
  });

  const dataValue = useMemo(() => ({ hoveredHotelId }), [hoveredHotelId]);
  const apiValue = useMemo(() => ({ onHoverStart, onHoverEnd }), [onHoverStart, onHoverEnd]);

  return (
    <HoverApiContext.Provider value={apiValue}>
      <HoverDataContext.Provider value={dataValue}>{children}</HoverDataContext.Provider>
    </HoverApiContext.Provider>
  );
});

AccommodationMapListSync.displayName = 'AccommodationMapListSync';
